import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { FilterRow, TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { formatQty, isPieceUom, qtyInputStep } from '../lib/quantity';
import { purchaseStatusLabel } from '../lib/purchaseLabels';
import {
  purchaseOrdersApi,
  type PurchaseOrderFormData,
  type PurchaseOrderListItem,
  type PurchaseOrdersMeta,
} from '../services/purchaseOrders';

type PurchaseOrderFormRow = {
  productId: number | '';
  productSearch: string;
  qty: string;
  uomItemId: number | '';
  siteId: number | '';
  waterPercent: string;
  nectarItemId: number | '';
  notes: string;
};

const EMPTY_FORM: PurchaseOrderFormData = {
  poNumber: '',
  supplierId: null,
  status: 'RECEIVED',
  orderDate: '',
  expectedDeliveryDate: null,
  notes: '',
  rows: [],
};

const EMPTY_ROW: PurchaseOrderFormRow = {
  productId: '',
  productSearch: '',
  qty: '',
  uomItemId: '',
  siteId: '',
  waterPercent: '',
  nectarItemId: '',
  notes: '',
};

function formatDateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function isBulkHoneyOrPollen(meta: PurchaseOrdersMeta, productId: number | ''): boolean {
  if (productId === '') return false;
  const product = meta.products.find((item) => item.id === Number(productId));
  const category = (product?.categoryLabel ?? '').trim().toLowerCase();
  return product?.kind === 'BULK' && (category === 'honung' || category === 'pollen');
}

function productOptionLabel(product: PurchaseOrdersMeta['products'][number]): string {
  return product.sku ? `${product.sku} - ${product.name}` : product.name;
}

export function ReceivingPage() {
  const [orders, setOrders] = useState<PurchaseOrderListItem[]>([]);
  const [meta, setMeta] = useState<PurchaseOrdersMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<PurchaseOrderFormData>(EMPTY_FORM);
  const [formRows, setFormRows] = useState<PurchaseOrderFormRow[]>([]);
  const [openProductSearchIndex, setOpenProductSearchIndex] = useState<number | null>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const { canRead } = useAuth();

  if (!canRead('receiving')) {
    return <Navigate to="/" replace />;
  }

  const loadAll = useCallback(async () => {
    try {
      const [listRes, metaRes] = await Promise.all([purchaseOrdersApi.list(), purchaseOrdersApi.meta()]);
      setOrders(listRes.purchaseOrders);
      setMeta(metaRes);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda inköp');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filteredOrders = useMemo(
    () =>
      orders.filter((order) => {
        const text = filter.trim().toLowerCase();
        const matchesText = !text || order.poNumber.toLowerCase().includes(text) || (order.supplierName ?? '').toLowerCase().includes(text);
        const matchesStatus = !statusFilter || order.status === statusFilter;
        return matchesText && matchesStatus;
      }),
    [filter, orders, statusFilter]
  );

  const defaultSiteId = meta?.sites.find((site) => site.name === 'Lager')?.id ?? meta?.sites[0]?.id ?? '';

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, orderDate: new Date().toISOString().slice(0, 10), status: 'RECEIVED' });
    setFormRows([{ ...EMPTY_ROW, siteId: defaultSiteId }]);
    setOpenProductSearchIndex(null);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setFormRows([]);
    setOpenProductSearchIndex(null);
  };

  const updateFormRow = (index: number, patch: Partial<PurchaseOrderFormRow>) => {
    setFormRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const handleProductSelect = (index: number, productId: number) => {
    const product = meta?.products.find((item) => item.id === productId);
    if (!product) return;
    updateFormRow(index, {
      productId,
      productSearch: productOptionLabel(product),
      uomItemId: product.defaultUomItemId ?? '',
      waterPercent: '',
      nectarItemId: '',
    });
    setOpenProductSearchIndex(null);
  };

  const handleSave = async () => {
    const rows = formRows
      .filter((row) => row.productId !== '' && row.qty.trim() !== '' && row.siteId !== '')
      .map((row) => ({
        productId: Number(row.productId),
        qty: Number(row.qty),
        uomItemId: row.uomItemId === '' ? null : Number(row.uomItemId),
        siteId: row.siteId === '' ? null : Number(row.siteId),
        waterPercent: row.waterPercent.trim() === '' ? null : Number(row.waterPercent),
        nectarItemId: row.nectarItemId === '' ? null : Number(row.nectarItemId),
        notes: row.notes.trim() || null,
      }))
      .filter((row) => row.productId > 0 && row.qty > 0 && (row.siteId ?? 0) > 0);

    const payload: PurchaseOrderFormData = { ...form, status: 'RECEIVED', rows };

    setSaving(true);
    try {
      const created = await purchaseOrdersApi.create(payload);
      toast.success('Inköp sparat och inlevererat');
      resetForm();
      await loadAll();
      navigate(`/receiving/${created.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara inköp');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !meta) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Inköp</h1>
          <p className="text-sm text-gray-600">Sparade inköp bokförs direkt i lagret.</p>
        </div>
        <WithWrite moduleCode="receiving">
          <button type="button" onClick={openCreate} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover">Nytt inköp</button>
        </WithWrite>
      </div>

      <div className="flex flex-wrap gap-2">
        <input type="text" placeholder="Filtrera på ordernummer eller leverantör" value={filter} onChange={(event) => setFilter(event.target.value)} className="w-72" />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-48">
          <option value="">Alla statusar</option>
          {meta.statuses.map((status) => <option key={status} value={status}>{purchaseStatusLabel(status)}</option>)}
        </select>
      </div>

      <TableWrapper>
        <TableHead>
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Inköp</th>
            <th className="px-2 py-1.5 text-left font-medium">Leverantör</th>
            <th className="px-2 py-1.5 text-left font-medium">Datum</th>
            <th className="px-2 py-1.5 text-left font-medium">Status</th>
            <th className="px-2 py-1.5 text-left font-medium">Rader</th>
            <th className="px-2 py-1.5 text-left font-medium">Beställt</th>
            <th className="px-2 py-1.5 text-left font-medium">Inlevererat</th>
          </tr>
          <FilterRow>
            <td colSpan={7} className="px-2 py-1" />
          </FilterRow>
        </TableHead>
        <tbody>
          {filteredOrders.map((order) => (
            <tr key={order.id} onClick={() => navigate(`/receiving/${order.id}`)} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50">
              <td className="px-2 py-1 font-medium">{order.poNumber}</td>
              <td className="px-2 py-1">{order.supplierName ?? '-'}</td>
              <td className="px-2 py-1">{formatDateOnly(order.orderDate)}</td>
              <td className="px-2 py-1">{purchaseStatusLabel(order.status)}</td>
              <td className="px-2 py-1">{order.rowCount}</td>
              <td className="px-2 py-1">{formatQty(order.totalQty)}</td>
              <td className="px-2 py-1">{formatQty(order.receivedQty)}</td>
            </tr>
          ))}
          {filteredOrders.length === 0 && <tr><td colSpan={7} className="px-2 py-3 text-sm text-gray-500">Inga inköp eller inga träffar.</td></tr>}
        </tbody>
      </TableWrapper>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <div>
                <h2 className="text-base font-semibold">Nytt inköp</h2>
                <p className="text-sm text-gray-600">När du sparar bokförs raderna direkt i lagret.</p>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div><label className="block text-sm text-gray-600">Inköpsnummer *</label><input value={form.poNumber} onChange={(event) => setForm((current) => ({ ...current, poNumber: event.target.value }))} className="w-full" /></div>
                <div><label className="block text-sm text-gray-600">Leverantör</label><select value={form.supplierId ?? ''} onChange={(event) => setForm((current) => ({ ...current, supplierId: event.target.value ? Number(event.target.value) : null }))} className="w-full"><option value="">-</option>{meta.suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}</select></div>
                <div><label className="block text-sm text-gray-600">Datum *</label><input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={form.orderDate} onChange={(event) => setForm((current) => ({ ...current, orderDate: event.target.value }))} className="w-full" /></div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2"><h3 className="font-medium">Rader</h3><button type="button" onClick={() => setFormRows((current) => [...current, { ...EMPTY_ROW, siteId: defaultSiteId }])} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover">Lägg till rad</button></div>
                <div className="space-y-2">
                  {formRows.map((row, index) => {
                    const selectedUomLabel = meta.uoms.find((uom) => uom.id === (row.uomItemId === '' ? null : row.uomItemId))?.label ?? null;
                    const showBulkFields = isBulkHoneyOrPollen(meta, row.productId);
                    const matchingProducts = meta.products.filter((product) => {
                      const search = row.productSearch.trim().toLowerCase();
                      if (!search) return true;
                      return `${product.sku ?? ''} ${product.name}`.toLowerCase().includes(search);
                    }).slice(0, 12);
                    return (
                      <div key={index} className="grid grid-cols-12 gap-2 rounded border border-gray-200 p-3">
                        <div className="col-span-12 md:col-span-4">
                          <label className="block text-sm text-gray-600">Produkt</label>
                          <div className="relative">
                            <input
                              value={row.productSearch}
                              placeholder="Sök artikelnummer eller namn"
                              onFocus={() => setOpenProductSearchIndex(index)}
                              onChange={(event) => updateFormRow(index, { productId: '', productSearch: event.target.value })}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setOpenProductSearchIndex((current) => current === index ? null : current);
                                  setFormRows((current) => current.map((entry, rowIndex) => {
                                    if (rowIndex !== index || entry.productId === '') return entry;
                                    const product = meta.products.find((candidate) => candidate.id === Number(entry.productId));
                                    return product ? { ...entry, productSearch: productOptionLabel(product) } : entry;
                                  }));
                                }, 120);
                              }}
                              className="w-full"
                            />
                            {openProductSearchIndex === index && (
                              <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                                {matchingProducts.map((product) => (
                                  <button
                                    key={product.id}
                                    type="button"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      handleProductSelect(index, product.id);
                                    }}
                                    className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                                  >
                                    {productOptionLabel(product)}
                                  </button>
                                ))}
                                {matchingProducts.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Ingen träff</div>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-span-6 md:col-span-2"><label className="block text-sm text-gray-600">Mängd</label><input type="number" min="0" step={qtyInputStep(selectedUomLabel)} value={row.qty} onKeyDown={(event) => { if (isPieceUom(selectedUomLabel) && (event.key === '.' || event.key === ',')) event.preventDefault(); }} onChange={(event) => updateFormRow(index, { qty: event.target.value })} className="w-full" /></div>
                        <div className="col-span-6 md:col-span-2"><label className="block text-sm text-gray-600">Enhet</label><select value={row.uomItemId} onChange={(event) => updateFormRow(index, { uomItemId: event.target.value ? Number(event.target.value) : '' })} className="w-full"><option value="">-</option>{meta.uoms.map((uom) => <option key={uom.id} value={uom.id}>{uom.label}</option>)}</select></div>
                        <div className="col-span-12 md:col-span-3"><label className="block text-sm text-gray-600">Lagerplats *</label><select value={row.siteId} onChange={(event) => updateFormRow(index, { siteId: event.target.value ? Number(event.target.value) : '' })} className="w-full"><option value="">Välj lagerplats</option>{meta.sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></div>
                        <div className="col-span-12 md:col-span-1 flex items-end justify-end"><button type="button" onClick={() => setFormRows((current) => current.filter((_, rowIndex) => rowIndex !== index))} className="rounded border border-red-200 px-2 py-1 text-sm text-red-700 hover:bg-red-50">Ta bort</button></div>
                        {showBulkFields && (
                          <>
                            <div className="col-span-12 md:col-span-3"><label className="block text-sm text-gray-600">Vattenhalt %</label><input type="number" min="0" max="100" step="0.01" value={row.waterPercent} onChange={(event) => updateFormRow(index, { waterPercent: event.target.value })} className="w-full" /></div>
                            <div className="col-span-12 md:col-span-3"><label className="block text-sm text-gray-600">Nektar</label><select value={row.nectarItemId} onChange={(event) => updateFormRow(index, { nectarItemId: event.target.value ? Number(event.target.value) : '' })} className="w-full"><option value="">-</option>{meta.nectars.map((nectar) => <option key={nectar.id} value={nectar.id}>{nectar.label}</option>)}</select></div>
                          </>
                        )}
                        <div className="col-span-12"><label className="block text-sm text-gray-600">Anteckningar</label><input value={row.notes} onChange={(event) => updateFormRow(index, { notes: event.target.value })} className="w-full" /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div><label className="block text-sm text-gray-600">Anteckningar</label><textarea value={form.notes ?? ''} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} className="min-h-[100px] w-full" /></div>
              <div className="flex gap-2"><button type="button" onClick={handleSave} disabled={saving} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50">{saving ? 'Sparar...' : 'Spara'}</button><button type="button" onClick={resetForm} className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100">Avbryt</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
